import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function ReturnDialog({ order, onClose, onSaved }) {
  const [reasons, setReasons] = useState([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState(order.items.map((it) => ({ product_variant_id: it.product_variant_id, sku: it.sku, max: it.qty - (it.returned_qty || 0), qty_sellable: 0, qty_damaged: 0, reason: "" })));

  useEffect(() => { api.get("/returns/reasons").then((r) => setReasons(r.data)); }, []);
  const upd = (i, patch) => setItems(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const totalSellable = items.reduce((s, it) => s + Number(it.qty_sellable || 0), 0);
  const totalDamaged = items.reduce((s, it) => s + Number(it.qty_damaged || 0), 0);

  const save = async () => {
    if (totalSellable + totalDamaged === 0) return toast.error("Isi jumlah retur minimal 1 item");
    if (items.some((it) => Number(it.qty_sellable || 0) + Number(it.qty_damaged || 0) > it.max)) return toast.error("Jumlah retur melebihi sisa qty terkirim");
    setSaving(true);
    try {
      await api.post("/returns", { order_id: order.id, notes, items: items.map((it) => ({ product_variant_id: it.product_variant_id, qty_sellable: Number(it.qty_sellable || 0), qty_damaged: Number(it.qty_damaged || 0), reason: it.reason || null })) });
      toast.success("Retur diproses"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display">Proses Retur · {order.order_no}</DialogTitle>
          <DialogDescription>Layak jual kembali ke Inventory Pusat (menambah Available). Rusak masuk Stok Non-Jual.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div className="grid grid-cols-12 gap-2 px-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            <span className="col-span-4">SKU</span><span className="col-span-1 text-right">Sisa</span><span className="col-span-2">Layak Jual</span><span className="col-span-2">Rusak</span><span className="col-span-3">Alasan</span>
          </div>
          {items.map((it, i) => (
            <div key={it.product_variant_id} className="grid grid-cols-12 items-center gap-2 rounded-lg border border-slate-200 p-2" data-testid={`return-item-${it.sku}`}>
              <span className="col-span-4 truncate font-mono text-xs font-semibold text-indigo-700">{it.sku}</span>
              <span className="col-span-1 text-right text-sm text-slate-500">{it.max}</span>
              <Input type="number" min={0} max={it.max} className="col-span-2" value={it.qty_sellable} disabled={it.max === 0} onChange={(e) => upd(i, { qty_sellable: e.target.value })} data-testid={`return-sellable-${i}`} />
              <Input type="number" min={0} max={it.max} className="col-span-2" value={it.qty_damaged} disabled={it.max === 0} onChange={(e) => upd(i, { qty_damaged: e.target.value })} data-testid={`return-damaged-${i}`} />
              <div className="col-span-3">
                <Select value={it.reason} onValueChange={(v) => upd(i, { reason: v })}>
                  <SelectTrigger className="h-9 text-xs" data-testid={`return-reason-${i}`}><SelectValue placeholder="Alasan" /></SelectTrigger>
                  <SelectContent>{reasons.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
        <div><Label className="text-xs text-slate-500">Catatan</Label><Textarea className="mt-1" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="return-notes" /></div>
        <div className="flex justify-between rounded-lg bg-slate-50 p-3 text-sm">
          <span className="text-emerald-700">Kembali ke stok: <b data-testid="return-total-sellable">{totalSellable}</b></span>
          <span className="text-rose-700">Non-jual: <b data-testid="return-total-damaged">{totalDamaged}</b></span>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-500" data-testid="save-return-btn">{saving ? "Memproses..." : "Proses Retur"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
